import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebase-config';
import { doc, getDoc, getDocs, collection } from 'firebase/firestore';


function LmsBatchDetails() {
    const navigate = useNavigate();
    const { packageId, batchId } = useParams();
    const [batch, setBatch] = useState(null);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!batchId) {
            setError('No batch ID provided');
            setLoading(false);
            return;
        }

        const fetchBatchAndCourses = async () => {
            try {
                // Step 1: Fetch batch by ID
                const batchDocRef = doc(db, 'lms_batch', batchId);
                const batchDocSnap = await getDoc(batchDocRef);

                if (!batchDocSnap.exists()) {
                    setError('Batch not found');
                    setLoading(false);
                    return;
                }

                const batchData = batchDocSnap.data();
                setBatch({ id: batchId, ...batchData });

                // Step 2: Fetch courses if available
                if (batchData.batchContainingCourses?.length > 0) {
                    const coursePromises = batchData.batchContainingCourses.map(async (courseId) => {
                        const courseDocRef = doc(db, 'lms_course', courseId);
                        const courseDocSnap = await getDoc(courseDocRef);
                        return courseDocSnap.exists() ? { id: courseId, ...courseDocSnap.data() } : null;
                    });
                    const courseData = await Promise.all(coursePromises);
                    setCourses(courseData.filter(course => course)); // Filter out null values
                }

                setLoading(false);
            } catch (err) {
                console.error('Failed to fetch batch or courses:', err);
                setError('Failed to fetch batch or courses');
                setLoading(false);
            }
        };

        fetchBatchAndCourses();
    }, [batchId]);


    const handleCourseClick = (courseId) => {
        navigate(`/package/${packageId}/${batchId}/${courseId}`);
    };

    if (loading) {
        return (
            <>

                <div className="bg-black min-h-screen text-white font-poppins p-6 flex flex-col items-center">
                    <p>Loading...</p>
                </div>

            </>
        );
    }

    if (error || !batch) {
        return (
            <>

                <div className="bg-black min-h-screen text-white font-poppins p-6 flex flex-col items-center">
                    <p>{error || 'Batch not found'}</p>
                </div>

            </>
        );
    }



    return (
        <>

            <div className="bg-black min-h-screen text-white font-poppins p-6 flex flex-col items-center">
                <div className="max-w-4xl w-full">

                    <div className='text-2xl text-yellow-600 mb-4'>Batch Page</div>
                    {/* Batch Details */}
                    <h1 className="text-[1.75rem] font-medium leading-9 bg-custom-gradient bg-clip-text text-transparent mb-4">
                        {batch.batchName || `Batch ${batch.id}`}
                    </h1>

                    <div className='flex gap-4 border border-gray-800 rounded-lg'>

                        <div className="w-1/2 h-auto  overflow-hidden p-6">
                            <img
                                src={batch.batchBanner}
                                alt={batch.batchName}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="p-6 w-1/2">
                            <div
                                className="text-xl font-normal text-white leading-9 mb-4"
                                dangerouslySetInnerHTML={{ __html: batch.batchDescription }}
                            />
                            <hr className="w-full border-t border-[rgba(231,233,235,0.3)] mb-4" />
                            <ul className="flex flex-col gap-2 list-none">
                                <li className="flex items-center gap-2 text-[1.2rem] font-normal text-white leading-[2.125rem]">
                                    <span className="w-2 h-2 rounded-full bg-custom-gradient"></span>
                                    Price: ₹{parseFloat(batch.batchPrice).toLocaleString('en-IN')}
                                </li>
                                <li className="flex items-center gap-2 text-[1.2rem] font-normal text-white leading-[2.125rem]">
                                    <span className="w-2 h-2 rounded-full bg-custom-gradient"></span>
                                    Created: {new Date(batch.createdAt).toLocaleDateString()}
                                </li>
                                <li className="flex items-center gap-2 text-[1.2rem] font-normal text-white leading-[2.125rem]">
                                    <span className="w-2 h-2 rounded-full bg-custom-gradient"></span>
                                    Updated: {new Date(batch.updatedAt).toLocaleDateString()}
                                </li>
                            </ul>
                        </div>

                    </div>

                    {/* Courses List */}
                    <h2 className="text-[1.75rem] mt-10 font-medium leading-9 bg-custom-gradient bg-clip-text text-transparent mb-4">
                        Included Courses
                    </h2>
                    <div className="flex flex-col gap-4">
                        {courses.length > 0 ? (
                            courses.map((course) => (
                                <div
                                    key={course.id}
                                    onClick={() => handleCourseClick(course.id)}
                                    className="bg-card-bg rounded-xl p-5 flex gap-6 cursor-pointer"
                                >
                                    <img
                                        src={course.courseBanner || "https://logo.gklite.in/anuragaffection.jpg"}
                                        className='w-24 h-auto rounded-lg'
                                        alt=""
                                    />

                                    <div className='flex flex-col gap-2'>
                                        <h3 className="text-[1.5rem] font-medium text-white leading-9">
                                            {course.courseName || `Course ${course.id}`}
                                        </h3>
                                        {course.courseDescription && (
                                            <div
                                                className="text-xl font-normal text-white leading-9 mb-4"
                                                dangerouslySetInnerHTML={{ __html: course.courseDescription }}
                                            />
                                        )}
                                        {course.duration && (
                                            <p className="text-[1.2rem] font-normal text-white leading-[2.125rem]">
                                                Duration: {course.duration}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-[1.2rem] font-normal text-white leading-[2.125rem]">
                                No courses found for this batch.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default LmsBatchDetails;